import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AppNav } from "@/components/AppNav";
import { Overline } from "@/components/dash/primitives";
import { Loader2, ArrowRight, CalendarDays, CheckCircle2 } from "lucide-react";

export default function ReportsArchive() {
  const [reports, setReports] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/reports").then(({ data }) => setReports(data)).catch(() => setReports([]));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <AppNav />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <Overline>Report archive</Overline>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-3">Monthly Reports</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Every published edition of the Asia–Australia Trade Briefing. Open any edition to read it interactively and
          download it as PDF or Excel.
        </p>

        {reports === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : reports.length === 0 ? (
          <div className="py-20 text-center text-slate-400">No reports yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10" data-testid="reports-list">
            {reports.map((r) => (
              <button
                key={r.id}
                data-testid={`report-card-${r.period}`}
                onClick={() => navigate(r.is_current ? "/" : `/report/${r.id}`)}
                className="text-left bg-white border border-slate-200 rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-slate-300 group"
              >
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDays className="w-4 h-4 text-slate-400" />
                  <span className="font-num text-sm font-semibold text-slate-800">{r.period}</span>
                  {r.is_current && (
                    <span className="ml-auto flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                      <CheckCircle2 className="w-3 h-3" /> Current
                    </span>
                  )}
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900 leading-tight">{r.title}</h3>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {r.window && <span className="font-num text-xs text-slate-400">Window: {r.window}</span>}
                  {r.prepared && <span className="font-num text-xs text-slate-400">Prepared: {r.prepared}</span>}
                </div>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed line-clamp-3">{r.lede}</p>
                {(r.index_metrics && r.index_metrics.length) ? (
                  <div className="grid grid-cols-3 gap-2 mt-4" data-testid={`report-metrics-${r.period}`}>
                    {r.index_metrics.slice(0, 3).map((m, i) => (
                      <div key={i} className="rounded-md bg-slate-100 px-2.5 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400 leading-tight">{m.label}</div>
                        <div className="font-num text-xs font-semibold text-slate-700 mt-0.5">{m.value}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {(r.stats || []).slice(0, 3).map((s, i) => (
                      <span key={i} className="font-num text-xs bg-slate-100 text-slate-700 rounded-md px-2 py-1">{s.value}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-sm text-primary font-medium mt-5 group-hover:gap-2.5 transition-all">
                  Open report <ArrowRight className="w-4 h-4" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
